import { FC, useState } from 'react';
import { Skeleton, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { FinancialHistoryPeriod } from 'api';
import { BasePeriod, useGetCompanyFinancialHistory } from 'services/hooks/companies';
import { CardBody, CardTitle, CardTitleRow, OutlinedCard, SacraTimestamp } from '../CompanyOverview.styles';
import { COLUMN_HEADERS, DEFAULT_VISIBLE_ROWS, formatPercent, formatRevenue } from './FinancialHistoryCard.constants';
import { FinancialHistoryCardProps } from './FinancialHistoryCard.props';
import {
  ControlsRow,
  EstBadge,
  FinancialTable,
  FinancialTableCell,
  FinancialTableHead,
  FinancialTableRow,
  ShowAllButton,
  StatBlock,
  StatLabel,
  StatValue,
  SummaryStatsRow,
} from './FinancialHistoryCard.styles';
import { TabButton, TabRow } from '../NewsAndPressReleasesCard/NewsAndPressReleasesCard.styles';

function isPositive(value: number | null | undefined): boolean | null {
  if (value == null) {
    return null;
  }
  return value >= 0;
}

const PeriodRow: FC<{ period: FinancialHistoryPeriod }> = ({ period }) => {
  const isEst = period.scenario === FinancialHistoryPeriod.scenario.PROJECTION;
  return (
    <FinancialTableRow>
      <FinancialTableCell>
        {period.period_label}
        {isEst && <EstBadge>Est.</EstBadge>}
      </FinancialTableCell>
      <FinancialTableCell>{formatRevenue(period.revenue)}</FinancialTableCell>
      <FinancialTableCell positive={isPositive(period.revenue_growth)}>
        {formatPercent(period.revenue_growth)}
      </FinancialTableCell>
      <FinancialTableCell>
        {period.sources.length === 0
          ? '—'
          : period.sources.map((src, i) => (
            <span key={src.link}>
              {i > 0 && ', '}
              <a href={src.link} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
                {src.publication || src.headline || src.link}
              </a>
            </span>
          ))}
      </FinancialTableCell>
    </FinancialTableRow>
  );
};

const FinancialHistoryCard: FC<FinancialHistoryCardProps> = ({ companyId }) => {
  const [basePeriod, setBasePeriod] = useState<BasePeriod>('year');
  const [expanded, setExpanded] = useState(false);

  const { data, isLoading } = useGetCompanyFinancialHistory(companyId, basePeriod);

  const periods = data?.periods ?? [];
  const visiblePeriods = expanded ? periods : periods.slice(0, DEFAULT_VISIBLE_ROWS);
  const hasData = periods.length > 0;

  const periodLabel = basePeriod === 'quarter' ? 'Quarters' : 'Years';

  return (
    <OutlinedCard variant="outlined">
      <CardBody>
        <CardTitleRow>
          <CardTitle>Financial History</CardTitle>
          <ControlsRow>
            {data?.sacra_fetched_at && (
              <SacraTimestamp>
                fetched{' '}
                {new Date(data.sacra_fetched_at).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </SacraTimestamp>
            )}
            <ToggleButtonGroup
              value={basePeriod}
              exclusive
              size="small"
              onChange={(_e, v: BasePeriod | null) => {
                if (v) {
                  setBasePeriod(v);
                  setExpanded(false);
                }
              }}>
              <ToggleButton value="quarter">Quarterly</ToggleButton>
              <ToggleButton value="year">Annual</ToggleButton>
            </ToggleButtonGroup>
          </ControlsRow>
        </CardTitleRow>

        <TabRow>
          <TabButton active>Sacra</TabButton>
          <TabButton disabled>Capital IQ</TabButton>
        </TabRow>

        {isLoading && <Skeleton variant="rectangular" width="100%" height={200} sx={{ borderRadius: 1 }} />}

        {!isLoading && !hasData && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 3 }}>
            No financial history available for this company.
          </Typography>
        )}

        {!isLoading && hasData && (
          <>
            <SummaryStatsRow>
              <StatBlock>
                <StatLabel>LTM Revenue</StatLabel>
                <StatValue>{formatRevenue(data?.ltm_revenue)}</StatValue>
              </StatBlock>
              <StatBlock>
                <StatLabel>YoY Growth</StatLabel>
                <StatValue>{formatPercent(data?.yoy_growth)}</StatValue>
              </StatBlock>
            </SummaryStatsRow>

            <FinancialTable>
              <thead>
                <FinancialTableRow>
                  {COLUMN_HEADERS.map(h => (
                    <FinancialTableHead key={h}>{h}</FinancialTableHead>
                  ))}
                </FinancialTableRow>
              </thead>
              <tbody>
                {visiblePeriods.map(period => (
                  <PeriodRow key={period.measurement_end_date} period={period} />
                ))}
              </tbody>
            </FinancialTable>

            {periods.length > DEFAULT_VISIBLE_ROWS && (
              <ShowAllButton variant="text" disableRipple onClick={() => setExpanded(prev => !prev)}>
                {expanded ? `Show Fewer ${periodLabel} ∧` : `Show All ${periods.length} ${periodLabel} ∨`}
              </ShowAllButton>
            )}
          </>
        )}
      </CardBody>
    </OutlinedCard>
  );
};

export { FinancialHistoryCard };
